import { useForm, usePage, router } from "@inertiajs/react"
import { useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FlashMessages } from "@/components/FlashMessages"
import { FormError } from "@/components/FormError"
import { Settings as SettingsIcon, Upload, Trash2, Building2 } from "lucide-react"
import { formatCNPJ, formatPhone, stripMask } from "@/lib/masks"
import type { AppPageProps } from "@/types"

interface SettingsAccount {
  id: number
  name: string
  tax_name: string | null
  tax_id: string | null
  phone: string | null
  street: string | null
  city: string | null
  state: string | null
  logo_url: string | null
}

interface SettingsPageProps extends AppPageProps {
  account: SettingsAccount
}

export default function Settings() {
  const { t } = useTranslation()
  const { auth, flash, account } = usePage<SettingsPageProps>().props
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(account.logo_url)

  const form = useForm({
    account: {
      name: account.name,
      tax_name: account.tax_name || "",
      tax_id: formatCNPJ(account.tax_id || ""),
      phone: formatPhone(account.phone || ""),
      street: account.street || "",
      city: account.city || "",
      state: account.state || "",
      logo: null as File | null,
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.transform((data) => ({
      ...data,
      account: {
        ...data.account,
        tax_id: stripMask(data.account.tax_id),
        phone: stripMask(data.account.phone),
      },
    }))
    form.post("/settings", {
      preserveState: true,
      forceFormData: true,
    })
  }

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) {
      form.setData("account", { ...form.data.account, logo: file })
      setLogoPreview(URL.createObjectURL(file))
    }
  }

  function handleRemoveLogo() {
    router.delete("/settings/logo")
  }

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t("settings.title")}</h2>
          <p className="text-muted-foreground">{t("settings.subtitle")}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5" />
              {t("settings.generalInfo")}
            </CardTitle>
            <CardDescription>{t("settings.generalInfoDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">{t("settings.nameLabel")}</Label>
                <Input
                  id="name"
                  value={form.data.account.name}
                  onChange={(e) => form.setData("account", { ...form.data.account, name: e.target.value })}
                  required
                />
                <FormError error={form.errors["account.name"]} />
              </div>

              <div className="space-y-2">
                <Label>{t("settings.logoLabel")}</Label>
                <div className="flex items-center gap-4">
                  {logoPreview && (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="w-16 h-16 object-contain rounded border"
                    />
                  )}
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {t("settings.uploadLogo")}
                    </Button>
                    {account.logo_url && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRemoveLogo}
                        className="text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t("settings.removeLogo")}
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">{t("settings.logoHint")}</p>
                <FormError error={form.errors["account.logo"]} />
              </div>

              <Button type="submit" disabled={form.processing}>
                {t("settings.saveChanges")}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {t("settings.companyInfo")}
            </CardTitle>
            <CardDescription>{t("settings.companyInfoDescription")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="tax_name">{t("settings.taxNameLabel")}</Label>
                <Input
                  id="tax_name"
                  value={form.data.account.tax_name}
                  onChange={(e) => form.setData("account", { ...form.data.account, tax_name: e.target.value })}
                  placeholder={t("settings.taxNamePlaceholder")}
                />
                <FormError error={form.errors["account.tax_name"]} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tax_id">{t("settings.taxIdLabel")}</Label>
                <Input
                  id="tax_id"
                  value={form.data.account.tax_id}
                  onChange={(e) => form.setData("account", { ...form.data.account, tax_id: formatCNPJ(e.target.value) })}
                  placeholder={t("settings.taxIdPlaceholder")}
                />
                <FormError error={form.errors["account.tax_id"]} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t("settings.phoneLabel")}</Label>
                <Input
                  id="phone"
                  value={form.data.account.phone}
                  onChange={(e) => form.setData("account", { ...form.data.account, phone: formatPhone(e.target.value) })}
                  placeholder={t("settings.phonePlaceholder")}
                />
                <FormError error={form.errors["account.phone"]} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="street">{t("settings.streetLabel")}</Label>
                <Input
                  id="street"
                  value={form.data.account.street}
                  onChange={(e) => form.setData("account", { ...form.data.account, street: e.target.value })}
                  placeholder={t("settings.streetPlaceholder")}
                />
                <FormError error={form.errors["account.street"]} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">{t("settings.cityLabel")}</Label>
                  <Input
                    id="city"
                    value={form.data.account.city}
                    onChange={(e) => form.setData("account", { ...form.data.account, city: e.target.value })}
                    placeholder={t("settings.cityPlaceholder")}
                  />
                  <FormError error={form.errors["account.city"]} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">{t("settings.stateLabel")}</Label>
                  <Input
                    id="state"
                    value={form.data.account.state}
                    onChange={(e) => form.setData("account", { ...form.data.account, state: e.target.value })}
                    placeholder={t("settings.statePlaceholder")}
                  />
                  <FormError error={form.errors["account.state"]} />
                </div>
              </div>

              <Button type="submit" disabled={form.processing}>
                {t("settings.saveChanges")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
